import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../../supabaseClient';
import AdminButton from '../../../components/admin/AdminButton';
import axios from 'axios';
import { FiUser, FiMapPin, FiBox, FiX } from 'react-icons/fi';
import { format, addWeeks } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'react-toastify';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import { generateAndUploadReceipt, generateAndUploadShippingForm } from '../../../utils/generateReceipt';
import { apiEndpoint } from '../../../config/api';
import { downloadFedexLabel } from '../../../services/fedexApi';
import { getCountryCode } from '../../../utils/countryUtils';


const ShipmentForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');
  const isEditMode = !!id;
  
  // Variables d'état pour le formulaire
  const [payments, setPayments] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFamilyModal, setShowFamilyModal] = useState(false);
  const [showNewFamilyForm, setShowNewFamilyForm] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [selectedFamilyMembers, setSelectedFamilyMembers] = useState([]);
  const [searchFamily, setSearchFamily] = useState('');
  // État pour création rapide d'un membre de famille depuis la modale
  const [newFamily, setNewFamily] = useState({ lastname: '', firstname: '', birth_date: '', email: '', phone: '' });
  const [isCreatingFamily, setIsCreatingFamily] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amount: '',
    currency: 'EUR',
    payment_status: 'pending',
    payment_method: 'card',
    transaction_id: `TR-${Date.now()}`
  });
  
  // Données fixes de l'expéditeur (Consulat)
  const shipperInfo = {
    name: "Consulat G Cote d'Ivoire Paris",
    company: "Consulat G Cote d'Ivoire Paris",
    address1: "19 rue de la pepiniere",
    address2: "",
    address3: "",
    city: "PARIS",
    postalCode: "75008",
    country: "France",
    phone: "0181726604",
    email: "consulatcistandard@outlook.fr"
  };

  // Création d'un membre de famille depuis la modale
  const handleCreateFamilyMemberInline = async (e) => {
    e?.preventDefault?.();
    try {
      if (!formData.user_id) {
        toast.error("Veuillez d'abord sélectionner un utilisateur");
        return;
      }
      if (!newFamily.lastname.trim() || !newFamily.firstname.trim()) {
        toast.error('Nom et prénom sont requis');
        return;
      }
      setIsCreatingFamily(true);
      const payload = {
        lastname: newFamily.lastname.trim(),
        firstname: newFamily.firstname.trim(),
        birth_date: newFamily.birth_date,
        email: newFamily.email.trim() || null,
        phone: newFamily.phone.trim() || null,
        parent_id: formData.user_id,
      };
      const { data, error } = await supabase
        .from('family_members')
        .insert([payload])
        .select('id, firstname, lastname, birth_date, email, phone')
        .single();
      if (error) throw error;
      toast.success('Membre de famille créé');
      // Recharger la liste pour cohérence, puis présélectionner le nouveau membre
      await loadFamilyMembers(formData.user_id);
      if (data) {
        setSelectedFamilyMembers(prev => {
          const exists = prev.some(m => m.id === data.id);
          return exists ? prev : [...prev, data];
        });
      }
      setNewFamily({ lastname: '', firstname: '', birth_date: '', email: '', phone: '' });
    } catch (err) {
      console.error('Erreur création membre de famille (inline):', err);
      toast.error(err?.message || 'Erreur lors de la création du membre de famille');
    } finally {
      setIsCreatingFamily(false);
    }
  };
  
  // Données fixes du colis
  const packageInfo = {
    packagingType: "FEDEX_ENVELOPE",
    weight: 0.5,
    weightUnit: "KG",
    count: 1
  };
  
  // Services FedEx disponibles
  const fedexServices = [
    { id: "STANDARD_OVERNIGHT", name: "FedEx Standard Overnight" },
    { id: "FEDEX_PRIORITY", name: "FedEx Priority" },
    { id: "FEDEX_EXPRESS_SAVER", name: "FedEx Express Saver" },
    { id: "FEDEX_GROUND", name: "FedEx Ground" },
    { id: "INTERNATIONAL_PRIORITY", name: "FedEx International Priority" },
    { id: "INTERNATIONAL_ECONOMY", name: "FedEx International Economy" }
  ];
  
  // Options de signature
  const signatureOptions = [
    { id: "DIRECT", name: "Signature directe requise" },
    { id: "INDIRECT", name: "Signature indirecte requise" },
    { id: "ADULT", name: "Signature d'un adulte requise" },
    { id: "NO_SIGNATURE_REQUIRED", name: "Aucune signature requise" }
  ];
  
  // Options d'enlèvement/dépôt
  const pickupOptions = [
    { id: "USE_SCHEDULED_PICKUP", name: "J'ai déjà programmé un enlèvement en ma localité" },
    { id: "DROPOFF_AT_FEDEX_LOCATION", name: "Je déposerai le colis dans une agence FedEx" }
  ];
  
  /**
   * État initial du formulaire d'expédition
   * Les champs d'adresse sont décomposés selon la structure de la table users
   */
  const [formData, setFormData] = useState({
    carrier: 'FEDEX', // Toujours FEDEX, non modifiable
    recipient_address: '',
    recipient_city: '',
    recipient_zipcode: '',
    recipient_country: 'France',
    recipient_email: '',
    status: 'created', // Par défaut à la création
    return_status: false,
    return_reason: '',
    weight: '',
    dimensions: {
      length: '',
      width: '',
      height: ''
    },
    recipient_firstname: '',
    recipient_lastname: '',
    recipient_birth_date: '',
    recipient_phone: '',
    service_id: '',
    service_name: '',
    service_price: 0,
    service_delivery_time: '',
    service_description: '',
    documents_to_be_sent: [],
    fedex_ship_date: format(new Date(), 'yyyy-MM-dd'),
    fedex_expected_delivery: '',
    fedex_response: null,
    
    // Options supplémentaires
    send_notification: true,
    notification_email: '',
    include_return_label: false,
    include_commercial_invoice: false,
    reference_number: '',
    
    // Membres de famille
    family_members: [],
    with_family: false,
    
    // Valeurs par défaut pour FedEx
    fedex_service_type: 'FEDEX_PRIORITY', // FedEx Priority par défaut
    fedex_signature_option: 'INDIRECT', // Signature indirecte requise par défaut
    pickup_type: 'USE_SCHEDULED_PICKUP' // Enlèvement programmé par défaut
  });
  
  // Suppression des états loading, error et success car nous utilisons uniquement les toasts
  const [loading, setLoading] = useState(false); // Conservé pour compatibilité avec le reste du code
  const [fedexApiStatus, setFedexApiStatus] = useState({ loading: false, error: null, success: false });
  // Variable estimatedCost supprimée car l'onglet Facturation a été retiré
  const [labelUrl, setLabelUrl] = useState('');
  
  // États pour la recherche d'utilisateurs
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  
  // États pour les actes consulaires
  const [consularActs, setConsularActs] = useState([]);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [searchDocument, setSearchDocument] = useState('');
  
  // Fonction pour charger les paiements d'un utilisateur
  // Déclaration de fetchUserPayments sans dépendance à loadShipmentDocuments
  const fetchUserPayments = useCallback(async (userId) => {
    if (!userId) return;
    try {
      const { data, error } = await supabase
        .from('payments')
        .select('*')
        .eq('user_id', userId)
        .eq('payment_status', 'succeeded')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      setPayments(data || []);
      
      // Si un seul paiement est trouvé, le sélectionner automatiquement
      if (data && data.length === 1) {
        const paymentId = data[0].id;
        setFormData(prev => ({
          ...prev,
          payment_id: paymentId
        }));
        
        // Nous ne pouvons pas appeler handlePaymentSelection ici car cela créerait une référence circulaire
        // À la place, nous chargeons directement les informations nécessaires
        try {
          const { data: payment, error: paymentError } = await supabase
            .from('payments')
            .select(`
              *,
              user:user_id (*)
            `)
            .eq('id', paymentId)
            .single();
          
          if (paymentError) throw paymentError;
          
          if (payment?.user) {
            // Préremplir les champs du destinataire avec les informations de l'utilisateur
            setFormData(prev => ({
              ...prev,
              recipient_firstname: payment.user.firstname || '',
              recipient_lastname: payment.user.lastname || '',
              recipient_birth_date: payment.user.birth_date || '',
              recipient_email: payment.user.email || '',
              recipient_phone: payment.user.phone || '',
              recipient_address: payment.user.address || '',
              recipient_zipcode: payment.user.zipcode || '',
              recipient_city: payment.user.city || '',
              recipient_country: payment.user.country || 'France'
            }));
          }
          
          // Vérifier si ce paiement a déjà une expédition associée
          // Au lieu d'appeler loadShipmentDocuments, on fait la requête directement ici
          try {
            // Rechercher une expédition existante pour ce paiement avec toutes les informations
            const { data: shipmentData, error: shipmentError } = await supabase
              .from('shipments')
              .select('*')
              .eq('payment_id', paymentId)
              .single();
            
            if (shipmentError && shipmentError.code !== 'PGRST116') { // PGRST116 = Aucune ligne trouvée
              throw shipmentError;
            }
            
            if (shipmentData) {
              // Traiter documents_to_be_sent pour s'assurer que c'est un tableau
              let documents = [];
              if (Array.isArray(shipmentData.documents_to_be_sent)) {
                documents = shipmentData.documents_to_be_sent;
              } else if (typeof shipmentData.documents_to_be_sent === 'string') {
                try {
                  documents = JSON.parse(shipmentData.documents_to_be_sent);
                } catch (e) {
                  documents = shipmentData.documents_to_be_sent ? [shipmentData.documents_to_be_sent] : [];
                }
              }
              
              // Mettre à jour les documents dans le formulaire directement dans formData
              setFormData(prev => ({
                ...prev,
                documents_to_be_sent: documents
              }));
            }
          } catch (shipmentError) {
            console.error('Erreur lors du chargement des documents de l\'expédition:', shipmentError);
          }
        } catch (error) {
          console.error('Erreur lors du chargement des informations du paiement:', error);
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des paiements:', error);
      toast.error('Erreur lors du chargement des paiements');
      setPayments([]);
    }
  }, []);

  // Fonction pour charger les membres de famille d'un utilisateur
  const loadFamilyMembers = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('family_members')
        .select('id, firstname, lastname, birth_date, email, phone')
        .eq('parent_id', userId);

      if (error) throw error;

      if (data && data.length > 0) {
        setFamilyMembers(data);
        
        // Si des membres de famille sont déjà sélectionnés (en mode édition)
        if (formData.family_members && formData.family_members.length > 0) {
          const selectedIds = formData.family_members.map(member => member.id);
          const preselectedMembers = data.filter(member => selectedIds.includes(member.id));
          setSelectedFamilyMembers(preselectedMembers);
        } else {
          setSelectedFamilyMembers([]);
        }
      } else {
        setFamilyMembers([]);
        setSelectedFamilyMembers([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des membres de famille:', error);
      toast.error('Erreur lors du chargement des membres de famille');
    }
  };

  // Calculer la date de livraison estimée (pour l'affichage)
  const getEstimatedDeliveryDate = () => {
    return formData.fedex_service_type === 'STANDARD_OVERNIGHT' 
      ? format(addWeeks(new Date(), 1), 'EEEE d MMMM yyyy', { locale: fr }) + ' jusqu\'à 18:00'
      : format(addWeeks(new Date(), 2), 'EEEE d MMMM yyyy', { locale: fr }) + ' jusqu\'à 18:00';
  };

  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  useEffect(() => {
    const loadUserFromUrl = async () => {
      if (userId) {
        try {
          // Récupérer les informations de l'utilisateur avec l'adresse
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select('*')
            .eq('id', userId)
            .single();
          
          if (userError) throw userError;
          
          if (userData) {
            // Mettre à jour le champ de recherche
            setSearchTerm(`${userData.firstname} ${userData.lastname} (${userData.email})`);
            
            // Mettre à jour les champs du formulaire
            setFormData(prev => ({
              ...prev,
              user_id: userId, // Ajouter l'ID de l'utilisateur pour activer le bouton "Ajouter des membres"
              recipient_firstname: userData.firstname,
              recipient_lastname: userData.lastname,
              recipient_birth_date: userData.birth_date || '',
              recipient_email: userData.email,
              recipient_phone: userData.phone || '',
              recipient_address: userData.address || '',
              recipient_zipcode: userData.zipcode || '',
              recipient_city: userData.city || '',
              recipient_country: userData.country || ''
            }));
            
            // Charger les paiements de l'utilisateur
            await fetchUserPayments(userId);
          }
        } catch (error) {
          console.error("Erreur lors du chargement de l'utilisateur:", error);
          toast.error("Erreur lors du chargement des informations de l'utilisateur");
        }
      }
    };
    
    loadUserFromUrl();
  }, [userId, fetchUserPayments]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Ne plus charger les paiements ici, on les chargera uniquement lors de la sélection d'un utilisateur
        
        // Si en mode édition, récupérer les données de l'expédition
        if (isEditMode) {
          // Récupérer les données de l'expédition avec jointure sur payments et users
          const { data: shipmentData, error: shipmentError } = await supabase
            .from('shipments')
            .select('*')
            .eq('id', id)
            .single();

          if (shipmentError) throw shipmentError;
          
          // Si on a un payment_id, récupérer les informations du paiement et de l'utilisateur
          let userData = null;
          let paymentData = null;
          
          if (shipmentData.payment_id) {
            // Récupérer les données du paiement
            const { data: payment, error: paymentError } = await supabase
              .from('payments')
              .select('*')
              .eq('id', shipmentData.payment_id)
              .single();
              
            if (paymentError) {
              console.error('Erreur lors de la récupération du paiement:', paymentError);
            } else {
              paymentData = payment;
              
              // Si on a un user_id dans le paiement, récupérer les informations de l'utilisateur
              if (payment.user_id) {
                const { data: user, error: userError } = await supabase
                  .from('users')
                  .select('*')
                  .eq('id', payment.user_id)
                  .single();
                  
                if (userError) {
                  console.error('Erreur lors de la récupération de l\'utilisateur:', userError);
                } else {
                  userData = user;
                  console.log('Données utilisateur récupérées:', userData);
                  
                  // Charger tous les paiements de cet utilisateur
                  const { data: userPayments, error: paymentsError } = await supabase
                    .from('payments')
                    .select('*')
                    .eq('user_id', user.id)
                    .eq('payment_status', 'succeeded')
                    .order('created_at', { ascending: false });
                  
                  if (paymentsError) {
                    console.error('Erreur lors du chargement des paiements de l\'utilisateur:', paymentsError);
                  } else {
                    console.log('Paiements de l\'utilisateur chargés:', userPayments);
                    setPayments(userPayments || []);
                  }
                }
              }
            }
          }
          
          // S'assurer que documents_to_be_sent est un tableau
          let documents = shipmentData.documents_to_be_sent;
          if (typeof documents === 'string') {
            try {
              documents = JSON.parse(documents);
            } catch (e) {
              documents = [];
            }
          }
          
          // Formater la date de livraison si elle existe
          let formattedData = {
            ...shipmentData,
            documents_to_be_sent: documents,
            delivery_date: shipmentData.delivery_date ? shipmentData.delivery_date.substring(0, 10) : ''
          };
          
          // Si l'adresse est stockée sous forme de chaîne unique, la décomposer
          if (formattedData.recipient_address && !formattedData.recipient_city) {
            // Logique pour décomposer l'adresse si nécessaire
            // À adapter selon le format actuel de l'adresse
            const addressParts = formattedData.recipient_address.split('\n');
            if (addressParts.length >= 3) {
              formattedData = {
                ...formattedData,
                recipient_address: addressParts[0],
                recipient_zipcode: addressParts[1].split(' ')[0],
                recipient_city: addressParts[1].split(' ').slice(1).join(' '),
                recipient_country: addressParts[2] || 'France'
              };
            }
          }
          
          // Injecter toutes les données de l'expédition dans le formulaire
          console.log('Données récupérées:', formattedData);
          
          // Extraire le nom et prénom du destinataire si nécessaire
          let recipient_firstname = '';
          let recipient_lastname = '';
          
          if (formattedData.recipient_name) {
            const nameParts = formattedData.recipient_name.split(' ');
            if (nameParts.length > 1) {
              recipient_firstname = nameParts[0];
              recipient_lastname = nameParts.slice(1).join(' ');
            } else {
              recipient_lastname = formattedData.recipient_name;
            }
          }
          
          // Mettre à jour tous les champs du formulaire avec les données récupérées
          // et les données utilisateur/paiement si disponibles
          
          // Préparer les données du formulaire en combinant les informations
          const formDataToSet = {
            // Données de base
            id: formattedData.id,
            user_id: userData?.id || formattedData.user_id || '',
            payment_id: formattedData.payment_id || '',
            tracking_number: formattedData.tracking_number || '',
            status: formattedData.status || '',
            delivery_date: formattedData.delivery_date || '',
            fedex_response: formattedData.fedex_response || '',
            documents_to_be_sent: formattedData.documents_to_be_sent || [],
            label_url: formattedData.label_url || '',
            carrier: formattedData.carrier || 'fedex',
            
            // Informations de paiement si disponibles
            transaction_id: paymentData?.transaction_id || '',
            amount: paymentData?.amount || '',
            currency: paymentData?.currency || 'EUR',
            payment_status: paymentData?.payment_status || 'pending',
            payment_method: paymentData?.payment_method || 'card',
            
            // Informations du destinataire - priorité aux données utilisateur si disponibles
            recipient_firstname: userData?.firstname || recipient_firstname,
            recipient_lastname: userData?.lastname || recipient_lastname,
            recipient_birth_date: userData?.birth_date || '',
            recipient_email: userData?.email || formattedData.recipient_email || '',
            recipient_address: userData?.address || formattedData.recipient_address || '',
            recipient_zipcode: userData?.zipcode || formattedData.recipient_zipcode || '',
            recipient_city: userData?.city || formattedData.recipient_city || '',
            recipient_country: userData?.country || formattedData.recipient_country || 'France',
            recipient_phone: userData?.phone || formattedData.recipient_phone || '',
            
            // Informations du colis
            package_type: formattedData.package_type || 'FEDEX_ENVELOPE',
            package_count: formattedData.package_count || 1,
            package_weight: formattedData.package_weight || 0.5,
            
            // Options supplémentaires
            family_members: formattedData.family_members || [],
            with_family: formattedData.with_family || false,
            opt_in: formattedData.opt_in || false,
            return_status: formattedData.return_status || false,
            return_reason: formattedData.return_reason || null,
          };
          
          console.log('Données combinées pour le formulaire:', formDataToSet);
          setFormData(formDataToSet);
          
          // Si on a des données utilisateur, on pré-remplit aussi le champ de recherche
          if (userData) {
            setSearchTerm(`${userData.firstname} ${userData.lastname}`);
            
            // Sélectionner automatiquement le paiement associé à l'expédition dans le dropdown
            if (formattedData.payment_id) {
              console.log('Sélection automatique du paiement:', formattedData.payment_id);
              // Le payment_id est déjà défini dans formData plus bas
            }
          }
          
          // Si des membres de famille sont associés, les charger également
          if (formattedData.family_members && formattedData.family_members.length > 0) {
            setSelectedFamilyMembers(formattedData.family_members);
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
        toast.error('Erreur lors du chargement des données. Veuillez réessayer.');
      } finally {
        setLoading(false);
        setIsSubmitting(false);
      }
    };
    
    fetchData();
  }, [id, isEditMode, userId]);

  // Gérer la recherche de contacts
  const handleSearchChange = async (e) => {
    // Vérifier que l'événement provient bien du champ de recherche de contact
    if (e.target.id !== 'contact_search') {
      return; // Ne rien faire si l'événement ne vient pas du champ de recherche
    }
    
    const value = e.target.value;
    setSearchTerm(value);
    
    if (value.length < 3) {
      setSearchResults([]);
      return;
    }
    
    // Annuler le timeout précédent pour éviter les requêtes multiples
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // Définir un nouveau timeout pour la recherche (debounce)
    setSearchTimeout(
      setTimeout(async () => {
        setIsSearching(true);
        
        try {
          const { data, error } = await supabase
            .from('users')
            .select('id, firstname, lastname, email, phone, address, city, zipcode, country, host_name')
            .or(`firstname.ilike.%${value}%,lastname.ilike.%${value}%,email.ilike.%${value}%`)
            .limit(10);
            
          if (error) throw error;
          
          setSearchResults(data || []);
        } catch (error) {
          console.error('Erreur lors de la recherche de contacts:', error);
          toast.error('Erreur lors de la recherche de contacts');
        } finally {
          setIsSearching(false);
        }
      }, 300) // Délai de 300ms avant la recherche
    );
  };
  
  // Fonction pour charger les documents d'une expédition existante
  const loadShipmentDocuments = useCallback(async (paymentId) => {
    if (!paymentId) return;
    
    try {
      // Rechercher une expédition existante pour ce paiement avec toutes les informations
      const { data: shipmentData, error } = await supabase
        .from('shipments')
        .select('*')
        .eq('payment_id', paymentId)
        .single();
      
      if (error && error.code !== 'PGRST116') { // PGRST116 = Aucune ligne trouvée
        throw error;
      }
      
      if (shipmentData) {
        // Traiter documents_to_be_sent pour s'assurer que c'est un tableau
        let documents = [];
        if (Array.isArray(shipmentData.documents_to_be_sent)) {
          documents = shipmentData.documents_to_be_sent;
        } else if (typeof shipmentData.documents_to_be_sent === 'string') {
          try {
            documents = JSON.parse(shipmentData.documents_to_be_sent);
            if (!Array.isArray(documents)) documents = [];
          } catch (e) {
            console.error('Erreur lors du parsing de documents_to_be_sent:', e);
            documents = [];
          }
        }
        
        // Préparer les données de l'expédition pour le formulaire
        const shipmentFormData = {
          // Informations de base
          tracking_number: shipmentData.tracking_number || '',
          carrier: shipmentData.carrier || 'FEDEX',
          status: shipmentData.status || 'created',
          delivery_date: shipmentData.delivery_date ? shipmentData.delivery_date.substring(0, 10) : '',
          return_status: shipmentData.return_status || false,
          return_reason: shipmentData.return_reason || '',
          opt_in: shipmentData.opt_in || false,
          
          // Informations du destinataire
          recipient_name: shipmentData.recipient_name || '',
          recipient_phone: shipmentData.recipient_phone || '',
          
          // Documents à envoyer
          documents_to_be_sent: documents,
          
          // Options FedEx (si disponibles)
          fedex_service_type: shipmentData.fedex_response?.service_type || 'FEDEX_PRIORITY',
          fedex_signature_option: shipmentData.fedex_response?.signature_option || 'INDIRECT',
          
          // Membres de famille
          family_members: shipmentData.family_members || [],
          with_family: shipmentData.with_family || false,
          
          // Autres champs
          notes: shipmentData.notes || ''
        };
        
        // Ne pas remplir les champs d'adresse à partir des données d'expédition
        // Ces champs doivent être remplis uniquement à partir des données utilisateur
        // Nous conservons donc les valeurs actuelles du formulaire pour ces champs
        
        // Mettre à jour le formulaire avec les données de l'expédition
        setFormData(prev => ({
          ...prev,
          ...shipmentFormData
        }));
        
        // Mettre à jour la date d'expédition si elle n'est pas définie
        if (!shipmentData.fedex_ship_date) {
          const today = new Date().toISOString().split('T')[0];
          setFormData(prev => ({
            ...prev,
            fedex_ship_date: today
          }));
        }
      } else {
        // Réinitialiser les champs si aucune expédition n'existe
        setFormData(prev => ({
          ...prev,
          documents_to_be_sent: [],
          tracking_number: '',
          status: 'pending',
          delivery_date: '',
          return_status: false,
          return_reason: ''
        }));
      }
    } catch (error) {
      console.error('Erreur lors du chargement des informations de l\'expédition:', error);
      toast.error('Erreur lors du chargement des informations de l\'expédition existante');
    }
  }, []);

  // La fonction fetchUserPayments a été déplacée plus haut dans le code

  // Gestion de la sélection d'un paiement
  const handlePaymentSelection = useCallback(async (paymentId) => {
    if (!paymentId) return;
    
    try {
      // Récupérer les informations du paiement avec les données de l'utilisateur
      const { data: payment, error: paymentError } = await supabase
        .from('payments')
        .select(`
          *,
          user:user_id (*)
        `)
        .eq('id', paymentId)
        .single();
      
      if (paymentError) throw paymentError;
      
      if (payment?.user) {
        const user = payment.user;
        
        // Vérifier s'il existe déjà une expédition pour ce paiement
        const { data: existingShipment, error: shipmentError } = await supabase
          .from('shipments')
          .select('*')
          .eq('payment_id', paymentId)
          .maybeSingle();
          
        if (shipmentError) throw shipmentError;
        
        // Mettre à jour le formulaire avec les informations de l'utilisateur
        // Pour les champs d'adresse, utiliser uniquement les données de l'utilisateur
        // Si host_name est présent, l'ajouter au début de l'adresse
        const addressWithHostName = user.host_name 
          ? `Chez ${user.host_name}, \n${user.address || ''}` 
          : user.address || '';
          
        setFormData(prev => ({
          ...prev,
          // Utiliser uniquement les données de l'utilisateur pour les champs d'adresse
          recipient_firstname: user.firstname || '',
          recipient_lastname: user.lastname || '',
          recipient_birth_date: user.birth_date || '',
          recipient_email: user.email || '',
          recipient_phone: user.phone || '',
          recipient_address: addressWithHostName,
          recipient_zipcode: user.zipcode || '',
          recipient_city: user.city || '',
          recipient_country: user.country || 'France',
          notification_email: user.email || '',
          user_id: user.id,
          // Mettre à jour les champs spécifiques à l'expédition si elle existe
          ...(existingShipment ? {
            tracking_number: existingShipment.tracking_number,
            status: existingShipment.status,
            delivery_date: existingShipment.delivery_date,
            fedex_response: existingShipment.fedex_response
          } : {})
        }));
        
        // Charger les documents de l'expédition existante si elle existe
        if (existingShipment) {
          await loadShipmentDocuments(paymentId);
        } else {
          // Réinitialiser les documents si aucune expédition n'existe
          setFormData(prev => ({
            ...prev,
            documents_to_be_sent: []
          }));
        }
      }
      
      // Afficher un message de confirmation
      toast.info('Informations du destinataire récupérées avec succès');
      
    } catch (error) {
      console.error('Erreur lors de la récupération des informations du paiement:', error);
      toast.error('Erreur lors du chargement des informations du paiement');
    }
  }, [loadShipmentDocuments]);

  // Supprimer un document de la liste
  const handleRemoveDocument = (index) => {
    setFormData(prev => ({
      ...prev,
      documents_to_be_sent: prev.documents_to_be_sent.filter((_, i) => i !== index)
    }));
  };
  
  // Ajouter un document à la liste
  const handleAddDocument = (document) => {
    // Vérifier si le document est déjà dans la liste
    const isAlreadyAdded = formData.documents_to_be_sent.some(doc => doc.act_id === document.id);
    
    if (!isAlreadyAdded) {
      setFormData(prev => ({
        ...prev,
        documents_to_be_sent: [
          ...prev.documents_to_be_sent,
          { act_id: document.id, act_title: document.title }
        ]
      }));
    }
  };
  
  // Ouvrir la modale de sélection de documents
  const handleOpenDocumentModal = () => {
    // Récupérer la liste des actes consulaires
    fetchConsularActs();
    setShowDocumentModal(true);
  };
  
  // Fermer la modale et réinitialiser la recherche
  const handleCloseDocumentModal = () => {
    setShowDocumentModal(false);
    setSearchDocument('');
  };
  
  // Récupérer la liste des actes consulaires
  const fetchConsularActs = async () => {
    try {
      const { data, error } = await supabase
        .from('consular_acts')
        .select('id, title, description, processing_fee')
        .eq('active', true)
        .order('title');
      
      if (error) throw error;
      
      setConsularActs(data || []);
    } catch (error) {
      console.error('Erreur lors de la récupération des actes consulaires:', error);
      toast.error('Erreur lors de la récupération des actes consulaires');
    }
  };

  // Fonction pour rechercher des utilisateurs
  // Cette fonction n'est plus utilisée car la recherche est maintenant gérée directement dans handleSearchChange
  // Elle est conservée en commentaire pour référence
  /*
  const searchUsers = async (query) => {
    if (query.length < 3) {
      setSearchResults([]);
      return;
    }
    
    try {
      setIsSearching(true);
      const { data, error } = await supabase
        .from('users')
        .select('id, firstname, lastname, email, phone, address, city, zipcode, country, host_name')
        .or(`firstname.ilike.%${query}%,lastname.ilike.%${query}%,email.ilike.%${query}%,phone.ilike.%${query}%`)
        .limit(10);
      
      if (error) throw error;
      
      setSearchResults(data || []);
    } catch (error) {
      console.error('Erreur lors de la recherche d\'utilisateurs:', error);
      toast.error('Erreur lors de la recherche d\'utilisateurs');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };
  */
  
  // Gestion du changement de la recherche avec debounce
  const handleChange = async (e) => {
    const { name, value } = e.target;
    
    // Mettre à jour la valeur du champ
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Si c'est le champ de paiement qui change, charger les infos utilisateur et documents
    if (name === 'payment_id' && value) {
      await handlePaymentSelection(value);
    }
    
    // Réinitialiser la sélection si le champ est vidé et qu'il s'agit du payment_id
    if (name === 'payment_id' && value === '') {
      setFormData(prev => ({
        ...prev,
        recipient_firstname: '',
        recipient_lastname: '',
        recipient_birth_date: '',
        recipient_email: '',
        recipient_phone: ''
      }));
    }
    
    // Suppression de la logique de recherche utilisateur globale
    // La recherche est maintenant gérée uniquement par handleSearchChange sur le champ contact_search
  };
  

  
  // Gestion de l'ajout d'un paiement
  const handleAddPayment = () => {
    // Vérifier si un utilisateur est sélectionné
    if (!formData.user_id) {
      toast.error("Veuillez d'abord sélectionner un utilisateur");
      return;
    }
    
    // Générer un nouvel ID de transaction
    setPaymentData(prev => ({
      ...prev,
      transaction_id: `TR-${Date.now()}`
    }));
    
    // Ouvrir la modale de paiement
    setShowPaymentModal(true);
  };
  
  // Enregistrer un nouveau paiement
  const handleSavePayment = async () => {
    // ID de toast pour le suivi de l'opération
    const toastId = "save-payment-toast";
    
    try {
      // Validation des données
      if (!paymentData.amount || parseFloat(paymentData.amount) <= 0) {
        toast.error("Veuillez entrer un montant valide");
        return;
      }
      
      // Afficher un toast de chargement
      toast.info("Enregistrement du paiement en cours...", {
        toastId,
        autoClose: false
      });
      
      // Préparer les données du paiement
      const paymentToInsert = {
        user_id: formData.user_id,
        transaction_id: paymentData.transaction_id,
        amount: parseFloat(paymentData.amount),
        currency: paymentData.currency,
        payment_status: paymentData.payment_status,
        payment_method: paymentData.payment_method
      };
      
      // Essayer d'insérer avec un mécanisme de réessai
      let attempts = 0;
      const maxAttempts = 3;
      let success = false;
      let data = null;
      let error = null;
      
      while (attempts < maxAttempts && !success) {
        attempts++;
        
        try {
          // Créer le paiement dans la base de données
          const result = await supabase
            .from('payments')
            .insert([paymentToInsert])
            .select();
          
          if (result.error) {
            error = result.error;
            // Attendre avant de réessayer (backoff exponentiel)
            if (attempts < maxAttempts) {
              await new Promise(resolve => setTimeout(resolve, 1000 * attempts));
            }
          } else {
            data = result.data;
            success = true;
          }
        } catch (err) {
          error = err;
          // Attendre avant de réessayer
          if (attempts < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, 1000 * attempts));
          }
        }
      }
      
      // Vérifier le résultat final après les tentatives
      if (!success) {
        throw error || new Error("Erreur lors de l'enregistrement du paiement après plusieurs tentatives");
      }
      
      // Ajouter le nouveau paiement à la liste et le sélectionner
      if (data && data.length > 0) {
        const newPayment = data[0];
        setPayments([newPayment, ...payments]);
        setFormData(prev => ({
          ...prev,
          payment_id: newPayment.id
        }));
        // Génération et upload des documents PDF (reçu et formulaire) comme dans Confirmation.js
        try {
          toast.info('Génération des documents (reçu et formulaire)...', { toastId: 'doc-gen', autoClose: false });
          const paymentMethodLabel = (paymentData.payment_method === 'card')
            ? 'Carte bancaire'
            : (paymentData.payment_method === 'cash' ? 'Espèces' : paymentData.payment_method);
          const userDetails = {
            transaction_id: paymentData.transaction_id,
            actTitle: 'Passeport',
            amount: parseFloat(paymentData.amount),
            paymentMethod: paymentMethodLabel,
            // Utiliser les champs disponibles dans le formulaire pour représenter le payeur
            userName: `${formData.recipient_firstname || ''} ${formData.recipient_lastname || ''}`.trim() || 'Client',
            userLastName: formData.recipient_lastname || '',
            userFirstName: formData.recipient_firstname || '',
            userBirthDate: formData.recipient_birth_date || '',
            userEmail: formData.recipient_email || 'Non spécifié',
            userPhone: formData.recipient_phone || 'Non spécifié',
            userAddress: formData.recipient_address || 'Adresse non renseignée',
            userCity: formData.recipient_city || 'Ville non renseignée',
            userZipcode: formData.recipient_zipcode || 'Code postal non renseigné',
            userCountry: formData.recipient_country || 'Pays non renseigné',
            hostName: formData.host_name || '',
            withFamily: formData.family_members.length > 0,
            familyMembers: formData.family_members
          };
          await generateAndUploadReceipt(userDetails);
          await generateAndUploadShippingForm(userDetails);
          toast.update('doc-gen', { render: 'Documents générés avec succès.', type: 'success', autoClose: 3000 });
        } catch (docErr) {
          console.error('Erreur génération documents (ShipmentForm admin):', docErr);
          toast.update('doc-gen', { render: "Paiement enregistré, mais la génération des documents a échoué.", type: 'error', autoClose: 5000 });
        }
        
        // Mettre à jour le toast
        toast.update(toastId, {
          render: "Paiement enregistré avec succès",
          type: "success",
          autoClose: 3000
        });
        
        setShowPaymentModal(false);
        
        // Réinitialiser le formulaire de paiement
        setPaymentData({
          amount: '',
          currency: 'EUR',
          payment_status: 'pending',
          payment_method: 'card',
          transaction_id: `TR-${Date.now()}`
        });
      }
    } catch (error) {
      console.error("Erreur lors de l'enregistrement du paiement:", error);
      
      // Mettre à jour le toast avec l'erreur
      toast.update(toastId, {
        render: `Erreur: ${error.message || "Problème de connexion au serveur"}`,
        type: "error",
        autoClose: 5000
      });
    }
  };
  
  // Sélection d'un utilisateur
  const handleSelectUser = async (user) => {
    setSearchTerm(`${user.firstname} ${user.lastname} (${user.email})`);
    setSearchResults([]);
    
    // Récupérer les informations complètes de l'utilisateur pour avoir l'adresse
    try {
      const { data: userData, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (error) throw error;
      
      if (userData) {
        // Mettre à jour les champs du formulaire avec toutes les informations d'adresse
        // Si host_name est présent, l'ajouter au début de l'adresse
        const addressWithHostName = userData.host_name 
          ? `${userData.host_name}\n${userData.address || ''}` 
          : userData.address || '';
          
        setFormData(prev => ({
          ...prev,
          recipient_firstname: userData.firstname || '',
          recipient_lastname: userData.lastname || '',
          recipient_birth_date: userData.birth_date || '',
          recipient_email: userData.email || '',
          recipient_phone: userData.phone || '',
          recipient_address: addressWithHostName,
          recipient_zipcode: userData.zipcode || '',
          recipient_city: userData.city || '',
          recipient_country: userData.country || 'France',
          user_id: userData.id // Stocker l'ID de l'utilisateur pour référence
        }));
      }
    } catch (error) {
      console.error("Erreur lors du chargement des détails de l'utilisateur:", error);
      toast.error("Erreur lors du chargement de l'adresse de l'utilisateur");
    }
    
    // Charger les paiements de l'utilisateur
    try {
      const { data, error } = await supabase
        .from('payments')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      setPayments(data || []);
      
      // Si des paiements sont trouvés, sélectionner automatiquement le plus récent
      if (data && data.length > 0) {
        const paymentId = data[0].id;
        setFormData(prev => ({
          ...prev,
          payment_id: paymentId
        }));
        
        // Appeler handlePaymentSelection pour charger les détails du paiement
        await handlePaymentSelection(paymentId);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des paiements:', error);
      toast.error('Erreur lors du chargement des paiements');
      setPayments([]);
    }
    
    // Charger les membres de famille de l'utilisateur si disponible
    if (user.id) {
      try {
        await loadFamilyMembers(user.id);
      } catch (error) {
        console.error('Erreur lors du chargement des membres de famille:', error);
      }
    }
  };
  
  // Fonction utilitaire pour tronquer une chaîne à une longueur maximale
  const truncateString = (str, maxLength) => {
    if (!str || typeof str !== 'string') return str;
    return str.length > maxLength ? str.substring(0, maxLength) : str;
  };

  /**
   * Supprime les accents/diacritiques d'une chaîne pour éviter que FedEx ne les remplace par "..."
   * N'altère que les chaînes (retourne la valeur d'origine sinon).
   * Exemple: "Élève à Cœur" -> "Eleve a Coeur"
   */
  const removeAccents = (value) => {
    if (typeof value !== 'string') return value;
    // Normalisation Unicode puis suppression des diacritiques
    return value.normalize('NFD').replace(/\p{Diacritic}+/gu, '');
  };

  // Fonction utilitaire pour formater les données selon les spécifications FedEx
  const formatFedExData = (data) => {
    // Normaliser les champs texte (sans accents) pour expéditeur et destinataire
    const cleanedShipper = {
      address1: removeAccents(data.shipper.address1),
      city: removeAccents(data.shipper.city),
      postalCode: data.shipper.postalCode,
      country: data.shipper.country,
      name: removeAccents(data.shipper.name),
      phone: data.shipper.phone,
      email: data.shipper.email
    };

    const cleanedRecipient = {
      address: removeAccents(data.recipient.address),
      city: removeAccents(data.recipient.city),
      zipcode: data.recipient.zipcode,
      country: data.recipient.country,
      firstname: removeAccents(data.recipient.firstname),
      lastname: removeAccents(data.recipient.lastname),
      phone: data.recipient.phone,
      email: data.recipient.email
    };

    return {
      requestedShipment: {
        shipper: {
          address: {
            streetLines: splitAddressLine(truncateString(cleanedShipper.address1, 70)),
            city: truncateString(cleanedShipper.city, 30),
            stateOrProvinceCode: "",
            postalCode: cleanedShipper.postalCode,
            countryCode: (() => {
              const code = getCountryCode(cleanedShipper.country);
              if (!code) throw new Error('Pays expéditeur invalide: utilisez un code ISO-2 (ex: FR)');
              return code;
            })()
          },
          contact: {
            personName: truncateString(cleanedShipper.name, 70),
            phoneNumber: truncateString(cleanedShipper.phone, 15),
            emailAddress: truncateString(cleanedShipper.email, 80)
          }
        },
        recipients: [{
          address: {
            streetLines: splitAddressLine(truncateString(cleanedRecipient.address, 70)),
            city: truncateString(cleanedRecipient.city, 30),
            stateOrProvinceCode: "",
            postalCode: cleanedRecipient.zipcode,
            countryCode: (() => {
              const code = getCountryCode(cleanedRecipient.country);
              if (!code) throw new Error('Pays destinataire invalide: utilisez un code ISO-2 (ex: FR)');
              return code;
            })()
          },
          contact: {
            personName: truncateString(`${cleanedRecipient.firstname} ${cleanedRecipient.lastname}`, 70),
            phoneNumber: truncateString(cleanedRecipient.phone || "", 15),
            emailAddress: truncateString(cleanedRecipient.email || "", 80)
          }
        }],
        pickupType: "DROPOFF_AT_FEDEX_LOCATION",
        serviceType: data.serviceType || "FEDEX_PRIORITY",
        packagingType: data.packagingType,
        totalWeight: data.totalWeight,
        preferredCurrency: "EUR",
        shipDateStamp: new Date().toISOString().split('T')[0],
        labelSpecification: {
          imageType: "PDF",
          labelStockType: "PAPER_85X11_TOP_HALF_LABEL"
        },
        requestedPackageLineItems: [{
          weight: {
            units: data.weightUnit,
            value: data.weight
          },
          customerReferences: [{
            customerReferenceType: "CUSTOMER_REFERENCE",
            value: `Payment ID: ${data.paymentId || ''}`.substring(0, 40)
          }]
        }],
        shippingDocumentSpecification: {
          shippingDocumentTypes: ["COMMERCIAL_INVOICE"],
          commercialInvoiceDetail: {
            documentFormat: {
              stockType: "PAPER_LETTER",
              docType: "PDF"
            }
          }
        },
        signatureOption: "INDIRECT"
      },
      accountNumber: {
        value: process.env.REACT_APP_FEDEX_ACCOUNT_NUMBER
      }
    };
  };

  // Fonction utilitaire pour diviser une chaîne en segments de longueur maximale
  const splitAddressLine = (address, maxLength = 35) => {
    if (!address) return [];
    
    const words = address.split(/\s+/);
    const lines = [];
    let currentLine = '';
    
    words.forEach(word => {
      if (currentLine.length + word.length + 1 <= maxLength) {
        currentLine += (currentLine ? ' ' : '') + word;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    });
    
    if (currentLine) lines.push(currentLine);
    return lines;
  };

  /**
   * Télécharge l'étiquette FedEx (PDF) depuis une URL et l'upload dans Supabase Storage.
   * Le fichier est nommé selon: "etiquette-<trackingNumber>.pdf".
   *
   * @param {string} labelUrl - URL fournie par FedEx pour l'étiquette.
   * @param {string} trackingNumber - Numéro de suivi FedEx.
   * @returns {Promise<{ path: string } | null>} Le chemin stocké ou null si échec.
   */
  const downloadAndUploadFedexLabel = async (labelUrl, trackingNumber) => {
    try {
      // 0) Paramètres requis
      if (!labelUrl || !trackingNumber) {
        console.warn('downloadAndUploadFedexLabel: paramètres invalides', { labelUrl, trackingNumber });
        return null;
      }

      // 1) Proxy backend (recommandé) pour éviter CORS côté client
      try {
        const proxyUrl = apiEndpoint('/api/fedex/labels/proxy-upload');
        const proxyRes = await axios.post(proxyUrl, { labelUrl, trackingNumber }, {
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token || ''}` }
        });
        if (proxyRes?.data?.path) {
          toast.success('Étiquette FedEx enregistrée dans Supabase', { toastId: `label-upload-${trackingNumber}` });
          return { path: proxyRes.data.path };
        }
      } catch (proxyErr) {
        console.warn('Proxy upload indisponible, fallback client-side:', proxyErr?.message);
      }

      // 2) Fallback client: peut échouer (CORS) selon les headers FedEx
      const res = await fetch(labelUrl, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
        },
        cache: 'no-store',
        mode: 'cors'
      });

      if (!res.ok) {
        throw new Error(`Échec du téléchargement de l'étiquette (HTTP ${res.status})`);
      }

      const blob = await res.blob();
      const filePath = `etiquette-${trackingNumber}.pdf`;

      const { data, error } = await supabase.storage
        .from('etiquettes')
        .upload(filePath, blob, {
          contentType: 'application/pdf',
          upsert: true,
        });

      if (error) throw error;

      toast.success('Étiquette FedEx enregistrée dans Supabase', { toastId: `label-upload-${trackingNumber}` });
      return { path: data?.path || filePath };
    } catch (err) {
      console.error('Erreur upload étiquette Supabase:', err);
      toast.warning("Expédition créée, mais l'étiquette n'a pas pu être stockée", { toastId: `label-upload-failed-${trackingNumber}` });
      return null;
    }
  };

  const handleDownloadFedexLabel = async (event) => {
    event.preventDefault();
    try {
      const blob = await downloadFedexLabel(labelUrl);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `etiquette-fedex-${formData.tracking_number || 'expedition'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || "Impossible de télécharger l'étiquette FedEx";
      toast.error(message);
    }
  };

  // Fonction pour valider une expédition FedEx via l'API
  const validateFedExShipment = async () => {
    const requiredFields = [
      { field: 'recipient_firstname', label: 'Prénom du destinataire' },
      { field: 'recipient_lastname', label: 'Nom du destinataire' },
      { field: 'recipient_address', label: 'Adresse du destinataire' },
      { field: 'recipient_zipcode', label: 'Code postal' },
      { field: 'recipient_city', label: 'Ville' },
      { field: 'recipient_country', label: 'Pays' }
    ];

    const missingFields = requiredFields
      .filter(({ field }) => !formData[field])
      .map(({ label }) => label);

    if (missingFields.length > 0) {
      throw new Error(`Champs obligatoires manquants : ${missingFields.join(', ')}`);
    }

    try {
      setFedexApiStatus({ loading: true, error: null, success: false });
      toast.dismiss();
      toast.info("Validation de l'expédition FedEx en cours...", { autoClose: false, toastId: "validate-shipment-toast" });

      const fedexData = formatFedExData({
        shipper: {
          address1: shipperInfo.address1,
          city: shipperInfo.city,
          postalCode: shipperInfo.postalCode,
          country: shipperInfo.country,
          name: shipperInfo.name,
          phone: shipperInfo.phone,
          email: shipperInfo.email
        },
        recipient: {
          address: formData.recipient_address,
          city: formData.recipient_city,
          zipcode: formData.recipient_zipcode,
          country: formData.recipient_country,
          firstname: formData.recipient_firstname,
          lastname: formData.recipient_lastname,
          phone: formData.recipient_phone,
          email: formData.recipient_email
        },
        serviceType: "FEDEX_PRIORITY",
        packagingType: packageInfo.packagingType,
        totalWeight: packageInfo.weight * (formData.package_count || packageInfo.count),
        weightUnit: packageInfo.weightUnit,
        weight: formData.package_weight || packageInfo.weight,
        paymentId: formData.payment_id
      });

      const fedexValidateApiUrl = apiEndpoint('/api/fedex/validate');

      const response = await axios.post(
        fedexValidateApiUrl,
        fedexData,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token || ''}`
          }
        }
      );

      setFedexApiStatus({ loading: false, error: null, success: false });
      toast.dismiss();

      if (response.data && response.data.output) {
        const alerts = response.data.output.alerts || [];
        const validationMessages = alerts.map(alert => alert.message).join('\n');
        toast.success("L'expédition est valide et prête à être créée.", { autoClose: 5000 });
        return { success: true, validationMessages };
      } else {
        setFedexApiStatus({ loading: false, error: "Format de réponse de validation FedEx inattendu", success: false });
        toast.error("Format de réponse de validation FedEx inattendu");
        return { success: false, error: "Format de réponse de validation FedEx inattendu" };
      }
    } catch (error) {
      console.error('Erreur lors de la validation de l\'expédition FedEx:', error);
      setFedexApiStatus({ loading: false, error: error.message || "Erreur lors de la validation de l'expédition FedEx", success: false });
      toast.dismiss();
      toast.error(error.message || "Erreur lors de la validation de l'expédition FedEx", { toastId: "validate-shipment-toast" });
      return { success: false, error: error.message || "Erreur lors de la validation de l'expédition FedEx" };
    }
  };

  // Fonction pour créer une expédition FedEx via l'API
  const createFedExShipment = async () => {
    // Vérifier que les champs obligatoires sont remplis
    if (!formData.recipient_firstname || !formData.recipient_lastname || !formData.recipient_address) {
      throw new Error("Le nom, le prénom et l'adresse du destinataire sont requis");
    }

    try {
      setFedexApiStatus({ loading: true, error: null, success: false });
      toast.dismiss();
      toast.info("Création de l'expédition FedEx en cours...", { autoClose: false, toastId: "shipment-toast" });
      
      // Pré-vérification supprimée pour éviter les erreurs 406 avec .single() inutile
      
      // Préparer les données pour l'API FedEx en utilisant la fonction utilitaire
      const fedexData = formatFedExData({
        shipper: {
          address1: shipperInfo.address1,
          city: shipperInfo.city,
          postalCode: shipperInfo.postalCode,
          country: shipperInfo.country,
          name: shipperInfo.name,
          phone: shipperInfo.phone,
          email: shipperInfo.email
        },
        recipient: {
          address: formData.recipient_address,
          city: formData.recipient_city,
          zipcode: formData.recipient_zipcode,
          country: formData.recipient_country,
          firstname: formData.recipient_firstname,
          lastname: formData.recipient_lastname,
          phone: formData.recipient_phone,
          email: formData.recipient_email
        },
        serviceType: "FEDEX_PRIORITY",
        packagingType: packageInfo.packagingType,
        totalWeight: packageInfo.weight * (formData.package_count || packageInfo.count),
        weightUnit: packageInfo.weightUnit,
        weight: formData.package_weight || packageInfo.weight,
        paymentId: formData.payment_id
      });
      
      // Ajouter l'option de réponse de l'étiquette
      fedexData.labelResponseOptions = "URL_ONLY";
      
      const fedexApiUrl = apiEndpoint('/api/fedex/shipments');
      
      const response = await axios.post(
        fedexApiUrl,
        fedexData,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token || ''}`
          }
        }
      );
      
      // Mettre à jour le statut de l'API FedEx
      setFedexApiStatus({ loading: false, error: null, success: true });
      toast.dismiss();
      toast.success("Expédition FedEx créée avec succès", { toastId: "shipment-toast" });
      
      // Extraire les informations importantes de la réponse
      if (response.data?.output?.output?.transactionShipments?.[0]) {
        const shipment = response.data.output.output.transactionShipments[0];
        const trackingNumber = shipment.masterTrackingNumber;
        const labelUrl = shipment.pieceResponses?.[0]?.packageDocuments?.[0]?.url;
        
        // Mettre à jour ou créer l'expédition dans la base de données
        let existingShipment = null;
        
        // Vérifier si une expédition existe déjà pour ce paiement
        if (formData.payment_id) {
          const { data: shipments, error } = await supabase
            .from('shipments')
            .select('*')
            .eq('payment_id', formData.payment_id)
            .limit(1);
          if (!error && shipments?.[0]) {
            existingShipment = shipments[0];
          }
        }

        // Vérifier si une expédition existe déjà pour ce tracking_number (éviter 409 unique constraint)
        if (!existingShipment && trackingNumber) {
          const { data: byTracking, error: trackErr } = await supabase
            .from('shipments')
            .select('*')
            .eq('tracking_number', trackingNumber)
            .limit(1);
          if (!trackErr && byTracking?.[0]) {
            existingShipment = byTracking[0];
          }
        }
        
        // Préparer les données pour l'enregistrement
        const shipmentData = {
          payment_id: formData.payment_id,
          documents_to_be_sent: formData.documents_to_be_sent || [],
          tracking_number: trackingNumber,
          recipient_name: `${formData.recipient_firstname} ${formData.recipient_lastname}`.trim(),
          recipient_address: generateFullAddress(formData),
          recipient_phone: formData.recipient_phone || null,
          carrier: 'FEDEX',
          status: 'created',
          fedex_response: response.data,
          label_url: labelUrl,
          updated_at: new Date().toISOString(),
          // Ajouter les membres de famille sélectionnés
          family_members: formData.family_members || [],
          with_family: formData.family_members && formData.family_members.length > 0
        };
        
        // Mettre à jour ou créer l'expédition dans la base de données
        let result;
        if (existingShipment) {
          result = await supabase
            .from('shipments')
            .update(shipmentData)
            .eq('id', existingShipment.id);
        } else {
          shipmentData.created_at = new Date().toISOString();
          if (!shipmentData.payment_id) {
            throw new Error('ID de paiement manquant pour créer une nouvelle expédition');
          }
          
          result = await supabase
            .from('shipments')
            .insert([shipmentData]);
        }
        
        if (result.error) {
          console.error('Erreur lors de la mise à jour de l\'expédition:', result.error);
          throw new Error('Erreur lors de la mise à jour de l\'expédition dans la base de données');
        }
        
        // Mettre à jour l'état local avec les informations de l'expédition
        setFormData(prev => ({
          ...prev,
          ...shipmentData,
          id: existingShipment?.id || result.data?.[0]?.id
        }));
        
        setLabelUrl(labelUrl);
        // Télécharger et stocker l'étiquette PDF dans Supabase Storage (bucket: etiquettes)
        // Nom du fichier: etiquette-[tracking_number].pdf
        try {
          await downloadAndUploadFedexLabel(labelUrl, trackingNumber);
        } catch (e) {
          // Sécurité supplémentaire: ne pas faire échouer le flux si l'upload échoue
          console.error('Erreur inattendue lors du stockage de l\'étiquette:', e);
        }
        return { 
          success: true, 
          trackingNumber, 
          labelUrl, 
          response: response.data,
          shipmentId: existingShipment?.id || result.data?.[0]?.id
        };
      } else {
        console.error('Format de réponse FedEx inattendu:', response.data);
        throw new Error('Format de réponse FedEx invalide ou données manquantes');
      }
    } catch (error) {
      console.error('Erreur lors de la création de l\'expédition FedEx:', error);
      const apiMessage = error?.response?.data?.message
        || error?.response?.data?.error
        || error?.message
        || "Erreur lors de la création de l'expédition FedEx";
      setFedexApiStatus({ 
        loading: false, 
        error: apiMessage,
        success: false 
      });
      throw new Error(apiMessage); // Propager un message exploitable par le backoffice
    }
  };

  /**
   * Génère une adresse complète à partir des champs détaillés
   * @param {Object} data - Les données du formulaire
   * @returns {string} L'adresse complète formatée
   */
  const generateFullAddress = (data) => {
    // Générer une adresse complète formatée à partir des champs du formulaire
    // Cette adresse sera stockée dans le champ recipient_address de la table shipments
    return `${data.recipient_address}\n${data.recipient_zipcode} ${data.recipient_city}\n${data.recipient_country}`;
  };

  // La fonction updateFamilyMembers a été fusionnée avec updateShipmentOnly
  // pour simplifier le code et éviter la duplication de fonctionnalités
  
  /**
   * Met à jour uniquement les informations de l'expédition dans la base de données sans faire appel à FedEx
   * @returns {Promise<Object>} Résultat de la mise à jour
   */
  const updateShipmentOnly = async () => {
    try {
      setIsSubmitting(true);
      toast.info('Mise à jour de l\'expédition...', {
        autoClose: false,
        toastId: 'update-shipment-toast'
      });
      
      // Validation des champs obligatoires
      if (!formData.recipient_firstname || !formData.recipient_lastname || 
          !formData.recipient_address || !formData.recipient_zipcode || !formData.recipient_city) {
        const errorMsg = 'Veuillez remplir tous les champs obligatoires (nom, prénom, adresse, code postal, ville)';
        toast.dismiss('update-shipment-toast');
        toast.error(errorMsg);
        return { success: false };
      }
      
      if (!formData.payment_id) {
        toast.dismiss('update-shipment-toast');
        toast.error('Veuillez sélectionner un paiement');
        return { success: false };
      }
      
      // Préparer les données pour l'enregistrement ou la mise à jour
      const shipmentData = {
        payment_id: formData.payment_id,
        documents_to_be_sent: formData.documents_to_be_sent || [],
        tracking_number: formData.tracking_number || `CD-${Date.now()}`,
        recipient_name: `${formData.recipient_firstname} ${formData.recipient_lastname}`.trim(),
        recipient_address: generateFullAddress(formData),
        recipient_phone: formData.recipient_phone || null,
        carrier: 'FEDEX',
        status: formData.status || 'pending',
        delivery_date: formData.delivery_date || null,
        return_status: formData.return_status || false,
        return_reason: formData.return_reason || null,
        opt_in: formData.send_notification || false,
        family_members: formData.family_members || [],
        with_family: formData.family_members && formData.family_members.length > 0,
        updated_at: new Date().toISOString()
      };
      
      let result;
      
      if (isEditMode && id) {
        // Mise à jour d'une expédition existante
        result = await supabase
          .from('shipments')
          .update(shipmentData)
          .eq('id', id);
      } else {
        // Création d'une nouvelle expédition
        shipmentData.created_at = new Date().toISOString();
        result = await supabase
          .from('shipments')
          .insert([shipmentData])
          .select();
          
        // Mettre à jour l'ID pour passer en mode édition
        if (result.data && result.data.length > 0) {
          const newId = result.data[0].id;
          // Mettre à jour l'URL pour être en mode édition
          navigate(`/admin/shipments/edit/${newId}`);
          // Le changement d'URL va recharger le composant en mode édition
        }
      }
      
      if (result.error) throw result.error;
      
      toast.dismiss('update-shipment-toast');
      toast.success('Expédition mise à jour avec succès');
      
      return { success: true };
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'expédition:', error);
      toast.dismiss('update-shipment-toast');
      toast.error(`Erreur: ${error.message || 'Erreur inconnue'}`);
      return { success: false, error };
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Activer l'état de chargement
    setIsSubmitting(true);
    
    // Faire défiler vers le haut de la page
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Afficher un toast de traitement
    toast.info('Traitement en cours...', {
      autoClose: false,
      toastId: 'shipment-processing'
    });
    
    try {
      // Validation des champs obligatoires
      if (!formData.recipient_firstname || !formData.recipient_lastname || 
          !formData.recipient_address || !formData.recipient_zipcode || !formData.recipient_city) {
        const errorMsg = 'Veuillez remplir tous les champs obligatoires (nom, prénom, adresse, code postal, ville)';
        toast.dismiss();
        toast.error(errorMsg, { toastId: "shipment-toast" });
        return;
      }
      
      // Le calcul du coût estimé a été supprimé car l'onglet Facturation a été retiré
      
      // Nous générerons l'adresse complète pour l'API FedEx directement dans la fonction createFedExShipment
      
      // Création de l'expédition FedEx (forcée à chaque enregistrement)
      let fedexResult = null;
      try {
        // Appel systématique à l'API FedEx, qu'un numéro de suivi existe déjà ou non
        fedexResult = await createFedExShipment();
        if (!fedexResult || !fedexResult.success) {
          const errorMsg = "Erreur lors de la création de l'expédition FedEx";
          toast.dismiss();
          toast.error(errorMsg, { toastId: "shipment-toast" });
          return;
        }
      } catch (error) {
        const errorMsg = `Erreur lors de l'appel à l'API FedEx: ${error.message || 'Erreur inconnue'}`;
        toast.dismiss();
        toast.error(errorMsg, { toastId: "shipment-toast" });
        return;
      }
      
      // Préparer les données pour l'enregistrement
      // N'utiliser que les colonnes qui existent réellement dans la table shipments
      const shipmentData = {
        payment_id: formData.payment_id,
        documents_to_be_sent: formData.documents_to_be_sent || [],
        tracking_number: fedexResult?.trackingNumber || formData.tracking_number,
        
        // Informations du destinataire (conformément au schéma de la table)
        recipient_name: `${formData.recipient_firstname} ${formData.recipient_lastname}`.trim(),
        recipient_address: formData.recipient_address,
        recipient_phone: formData.recipient_phone || null,
        
        // Informations d'expédition (conformément au schéma de la table)
        carrier: 'FEDEX',
        status: 'created',
        delivery_date: null,
        return_status: formData.return_status || false,
        return_reason: formData.return_reason || null,
        opt_in: formData.send_notification || false,
        
        // Données FedEx (conformément au schéma de la table)
        fedex_response: fedexResult?.response || formData.fedex_response || null,
        label_url: fedexResult?.labelUrl || formData.label_url || null,
        
        // Date de mise à jour
        updated_at: new Date().toISOString()
      };
      
      // Note: recipient_email, recipient_firstname, recipient_lastname, recipient_zipcode, recipient_city, recipient_country
      // ne sont pas des colonnes de la table shipments et ne doivent pas être incluses dans shipmentData

      // Création ou mise à jour dans Supabase
      let result;
      if (isEditMode) {
        // Mise à jour
        result = await supabase
          .from('shipments')
          .update(shipmentData)
          .eq('id', id);
      } else {
        // Création
        shipmentData.created_at = new Date().toISOString();
        result = await supabase
          .from('shipments')
          .insert([shipmentData]);
      }
      
      if (result.error) {
        throw result.error;
      }
      
      // Fermer le toast de traitement et afficher un toast de succès
      toast.dismiss();
      const successMsg = isEditMode ? 'Expédition mise à jour avec succès' : 'Expédition créée avec succès';
      toast.success(successMsg, {
        autoClose: 5000,
        toastId: "shipment-toast"
      });
      
      // Faire défiler vers le haut pour afficher le bouton de téléchargement
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Ne pas rediriger automatiquement afin de permettre le téléchargement du document
      // L'utilisateur reste sur cette page pour accéder au lien d'étiquette/document
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement de l\'expédition:', error);
      
      // Fermer le toast de traitement et afficher un toast d'erreur
      toast.dismiss();
      const errorMsg = `Une erreur est survenue: ${error.message || 'Erreur inconnue'}`;
      toast.error(errorMsg, {
        autoClose: 5000,
        toastId: "shipment-toast"
      });
      
      // Faire défiler vers le haut pour afficher l'erreur
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Suppression du loader qui masquait le formulaire
  
  return (
    <div className="container mx-auto px-4 py-8">
      <h2 className="text-2xl font-bold mb-6">
        {isEditMode ? 'Modifier une expédition' : 'Créer une expédition FedEx'}
      </h2>
      
      {/* Les messages d'erreur et de succès sont maintenant affichés uniquement via des toasts */}
      
      {/* Le statut de l'API FedEx est maintenant affiché uniquement via des toasts */}
      
      {fedexApiStatus.error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6" role="alert">
          <p className="font-bold">Erreur lors de la création de l&apos;expédition FedEx</p>
          <p>{fedexApiStatus.error}</p>
        </div>
      )}
      
      {fedexApiStatus.success && (
        <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-6" role="alert">
          <p className="font-bold">Expédition FedEx créée avec succès</p>
          <p>Numéro de suivi: {formData.tracking_number}</p>
          {labelUrl && (
            <p className="mt-2">
              <a 
                href="#download-fedex-label"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                onClick={handleDownloadFedexLabel}
              >
                Télécharger l&apos;étiquette d&apos;expédition
              </a>
              <span className="text-xs text-gray-500 ml-2">(Authentification sécurisée)</span>
            </p>
          )}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="bg-white shadow-md rounded-lg p-6">
        <Tabs selectedIndex={activeTab} onSelect={index => setActiveTab(index)} className="mb-6">
          <TabList className="flex border-b border-gray-200 mb-4">
            <Tab className="mr-2 py-2 px-4 text-center cursor-pointer rounded-t-lg border-b-2 border-transparent hover:text-blue-600 hover:border-blue-600 focus:outline-none">
              <div className="flex items-center">
                <FiUser className="mr-2" /> Informations générales
              </div>
            </Tab>
            <Tab className="mr-2 py-2 px-4 text-center cursor-pointer rounded-t-lg border-b-2 border-transparent hover:text-blue-600 hover:border-blue-600 focus:outline-none">
              <div className="flex items-center">
                <FiMapPin className="mr-2" /> Expédition
              </div>
            </Tab>
            <Tab className="mr-2 py-2 px-4 text-center cursor-pointer rounded-t-lg border-b-2 border-transparent hover:text-blue-600 hover:border-blue-600 focus:outline-none">
              <div className="flex items-center">
                <FiBox className="mr-2" /> Détails du colis
              </div>
            </Tab>

          </TabList>
          
          {/* Onglet 1: Informations générales */}
          <TabPanel>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Champ de recherche de contact */}
          <div className="relative">
            <div className="relative">
              <label className="block text-gray-700 text-sm font-bold mb-1" htmlFor="contact_search">
                Contact
              </label>
              <input
                id="contact_search"
                type="text"
                className="tracking-input w-full"
                placeholder="Rechercher un contact..."
                value={searchTerm}
                onChange={handleSearchChange}
                
                autoComplete="off"
              />
              {isSearching && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                </div>
              )}
            </div>
            
            {/* Liste déroulante des résultats */}
            {searchResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                {searchResults.map(user => (
                  <div
                    key={user.id}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    onClick={() => handleSelectUser(user)}
                  >
                    <div className="font-medium">{user.firstname} {user.lastname}</div>
                    <div className="text-sm text-gray-600">{user.email}</div>
                    {user.phone && <div className="text-sm text-gray-500">{user.phone}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-1" htmlFor="payment_id">
              Paiement
            </label>
            <div className="flex space-x-2">
              <select
                id="payment_id"
                name="payment_id"
                required
                className="tracking-input flex-grow text-base h-10"
                value={formData.payment_id}
                onChange={handleChange}
                
                style={{ minWidth: '250px' }}
              >
                <option value="">Sélection</option>
                {payments.map(payment => (
                  <option key={payment.id} value={payment.id}>
                    {Number(payment.amount).toFixed(2)}€ ({payment.created_at ? new Date(payment.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'Date inconnue'})
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="inline-flex items-center px-6 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 whitespace-nowrap h-10"
                onClick={() => handleAddPayment()}
              >
                Ajouter un paiement
              </button>
            </div>
          </div>
          
          {/* Le bloc Numéro de suivi a été supprimé car il est généré automatiquement */}
          
          {/* Le champ transporteur est masqué car toujours FEDEX */}
          <input type="hidden" name="carrier" value="FEDEX" />
          

          
          {/* Le champ Statut a été supprimé car non demandé */}
          
          {/* Valeur par défaut pour le statut */}
          <input type="hidden" name="status" value="created" />
          
          {/* Le champ Date de livraison a été supprimé car non demandé */}
          <input type="hidden" name="delivery_date" value="" />
          
          <div className="flex items-center mt-4 !hidden">
            <input
              id="return_status"
              name="return_status"
              type="checkbox"
              className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
              checked={formData.return_status}
              onChange={(e) => setFormData(prev => ({ ...prev, return_status: e.target.checked }))}
            />
            <label htmlFor="return_status" className="ml-2 block text-sm text-gray-900">
              Retour de l&apos;expédition
            </label>
          </div>
          
          {formData.return_status && (
            <div className="md:col-span-3">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="return_reason">
                Raison du retour
              </label>
              <textarea
                id="return_reason"
                name="return_reason"
                rows="2"
                className="tracking-input w-full"
                value={formData.return_reason || ''}
                onChange={handleChange}
              ></textarea>
            </div>
          )}
          
          <div className="md:col-span-3">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Informations du destinataire</h3>
          </div>
          
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="recipient_lastname">
              Nom *
            </label>
            <input
              id="recipient_lastname"
              name="recipient_lastname"
              type="text"
              required
              className="tracking-input w-full"
              value={formData.recipient_lastname}
              onChange={handleChange}
              
            />
          </div>
          
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="recipient_firstname">
              Prénom *
            </label>
            <input
              id="recipient_firstname"
              name="recipient_firstname"
              type="text"
              required
              className="tracking-input w-full"
              value={formData.recipient_firstname}
              onChange={handleChange}
              
            />
          </div>
          
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="recipient_email">
              Email *
            </label>
            <input
              id="recipient_email"
              name="recipient_email"
              type="email"
              required
              className="tracking-input w-full"
              value={formData.recipient_email}
              onChange={handleChange}
              
            />
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="recipient_birth_date">
              Date de naissance *
            </label>
            <input
              id="recipient_birth_date"
              name="recipient_birth_date"
              type="date"
              required
              max={new Date().toISOString().slice(0, 10)}
              className="tracking-input w-full"
              value={formData.recipient_birth_date}
              onChange={handleChange}
            />
          </div>
          
          <div className="md:col-span-3">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="recipient_address">
              Adresse *
            </label>
            <input
              id="recipient_address"
              name="recipient_address"
              type="text"
              required
              className="tracking-input w-full"
              value={formData.recipient_address}
              onChange={handleChange}
              
              placeholder="Numéro et nom de rue"
            />
          </div>
          
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="recipient_zipcode">
              Code postal *
            </label>
            <input
              id="recipient_zipcode"
              name="recipient_zipcode"
              type="text"
              required
              className="tracking-input w-full"
              value={formData.recipient_zipcode}
              onChange={handleChange}
              
            />
          </div>
          
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="recipient_city">
              Ville *
            </label>
            <input
              id="recipient_city"
              name="recipient_city"
              type="text"
              required
              className="tracking-input w-full"
              value={formData.recipient_city}
              onChange={handleChange}
              
            />
          </div>
          
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="recipient_country">
              Pays *
            </label>
            <input
              id="recipient_country"
              name="recipient_country"
              type="text"
              required
              className="tracking-input w-full"
              value={formData.recipient_country}
              onChange={handleChange}
              list="shipping-country-suggestions"
              placeholder="France ou FR"
            />
            <datalist id="shipping-country-suggestions">
              <option value="France" />
              <option value="Belgique" />
              <option value="Suisse" />
              <option value="Luxembourg" />
              <option value="Canada" />
              <option value="Côte d'Ivoire" />
            </datalist>
          </div>
            
          <div className="md:col-span-3">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="recipient_phone">
              Téléphone *
            </label>
            <input
              id="recipient_phone"
              name="recipient_phone"
              type="tel"
              required
              className="tracking-input w-full"
              value={formData.recipient_phone}
              onChange={handleChange}
              
            />
          </div>
          
          <div className="md:col-span-3 mb-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-md font-semibold text-gray-800">Membres de famille</h3>
              <button
                type="button"
                className="btn-modern btn-secondary-modern text-xs px-3 py-1 flex items-center"
                onClick={async () => {
                  // Vérifier si un utilisateur est sélectionné
                  if (!formData.user_id) {
                    toast.warning('Veuillez d\'abord sélectionner un contact');
                    return;
                  }
                  try {
                    // Charger les membres de famille et afficher la modale
                    await loadFamilyMembers(formData.user_id);
                    setShowFamilyModal(true);
                  } catch (error) {
                    console.error('Erreur lors du chargement des membres de famille:', error);
                    toast.error('Erreur lors du chargement des membres de famille');
                  }
                }}
                disabled={!formData.user_id}
              >
                Ajouter des membres
              </button>
            </div>
            
            {/* Affichage des membres de famille sélectionnés */}
            <div className="bg-gray-50 rounded-lg p-4 min-h-[60px] border border-gray-200">
              {selectedFamilyMembers.length === 0 ? (
                <span className="text-gray-500 italic">Aucun membre de famille sélectionné</span>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {selectedFamilyMembers.map(member => (
                    <div 
                      key={member.id} 
                      className="flex items-center bg-white px-3 py-1 rounded-full border border-gray-300 text-sm"
                    >
                      <span>{member.firstname} {member.lastname}</span>
                      <button 
                        type="button" 
                        className="ml-2 text-gray-500 hover:text-red-500 focus:outline-none"
                        onClick={() => {
                          // Retirer le membre de la sélection
                          setSelectedFamilyMembers(prev => prev.filter(m => m.id !== member.id));
                          
                          // Mettre à jour formData.family_members
                          setFormData(prev => ({
                            ...prev,
                            family_members: prev.family_members.filter(m => m.id !== member.id),
                            with_family: prev.family_members.filter(m => m.id !== member.id).length > 0
                          }));
                          
                          toast.info(`${member.firstname} ${member.lastname} retiré(e) de l'expédition`);
                        }}
                        aria-label={`Retirer ${member.firstname} ${member.lastname}`}
                      >
                        <FiX size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <div className="md:col-span-3">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Documents à envoyer
            </label>
            
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap gap-2 bg-gray-50 rounded-lg p-4 min-h-[60px]">
                {!formData.documents_to_be_sent || formData.documents_to_be_sent.length === 0 ? (
                  <span className="text-gray-500 italic">Aucun document</span>
                ) : (
                  Array.isArray(formData.documents_to_be_sent) && formData.documents_to_be_sent.map((doc, index) => (
                    <div 
                      key={doc.act_id || index} 
                      className="inline-flex items-center bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full"
                    >
                      <span>{doc.act_title || (typeof doc === 'string' ? doc : JSON.stringify(doc))}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveDocument(index)}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                        aria-label="Supprimer le document"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))
                )}
              </div>
              <button
                type="button"
                onClick={handleOpenDocumentModal}
                className="self-start text-blue-600 hover:text-blue-800 flex items-center text-sm font-medium"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Ajouter des documents
              </button>
            </div>
          </div>
        </div>
          </TabPanel>
          
          {/* Onglet 2: Options d'expédition */}
          <TabPanel>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="fedex_service_type">
                  Type de service FedEx
                </label>
                <select
                  id="fedex_service_type"
                  name="fedex_service_type"
                  required
                  className="tracking-input w-full"
                  value={formData.fedex_service_type}
                  onChange={handleChange}
                >
                  {fedexServices.map(service => (
                    <option key={service.id} value={service.id}>
                      {service.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="fedex_signature_option">
                  Option de signature
                </label>
                <select
                  id="fedex_signature_option"
                  name="fedex_signature_option"
                  required
                  className="tracking-input w-full"
                  value={formData.fedex_signature_option}
                  onChange={handleChange}
                >
                  {signatureOptions.map(option => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="fedex_ship_date">
                  Date d&apos;expédition
                </label>
                <input
                  id="fedex_ship_date"
                  name="fedex_ship_date"
                  type="date"
                  required
                  className="tracking-input w-full"
                  value={formData.fedex_ship_date}
                  onChange={handleChange}
                />
                <p className="text-sm text-gray-500 mt-1 hidden">
                  Estimation: {getEstimatedDeliveryDate()}
                </p>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="pickup_type">
                  Enlèvement/dépôt
                </label>
                <select
                  id="pickup_type"
                  name="pickup_type"
                  required
                  className="tracking-input w-full"
                  value={formData.pickup_type}
                  onChange={handleChange}
                >
                  {pickupOptions.map(option => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="flex items-center mt-4">
                <input
                  id="send_notification"
                  name="send_notification"
                  type="checkbox"
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  checked={formData.send_notification}
                  onChange={(e) =>
                    setFormData(prev => ({
                      ...prev,
                      // Coche/décoche l'option
                      send_notification: e.target.checked,
                      // Lorsqu'on coche, auto-remplir l'email de notification avec l'email du destinataire
                      notification_email: e.target.checked
                        ? (prev.recipient_email || prev.notification_email || '')
                        : prev.notification_email,
                    }))
                  }
                />
                <label htmlFor="send_notification" className="ml-2 block text-sm text-gray-900">
                  Envoyer une notification au destinataire
                </label>
              </div>
              
              {formData.send_notification && (
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="notification_email">
                    Email de notification
                  </label>
                  <input
                    id="notification_email"
                    name="notification_email"
                    type="email"
                    className="tracking-input w-full"
                    value={formData.notification_email}
                    onChange={handleChange}
                  />
                </div>
              )}
            </div>
          </TabPanel>

          {/* Onglet 3: Détails du colis */}
          <TabPanel>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="package_type">
                  Conditionnement
                </label>
                <select
                  id="package_type"
                  name="package_type"
                  className="tracking-input w-full"
                  value={formData.package_type || 'FEDEX_ENVELOPE'}
                  onChange={handleChange}
                  
                >
                  <option value="FEDEX_ENVELOPE">FedEx Envelope</option>
                  <option value="FEDEX_PAK">FedEx Pak</option>
                  <option value="FEDEX_BOX">FedEx Box</option>
                  <option value="FEDEX_TUBE">FedEx Tube</option>
                  <option value="YOUR_PACKAGING">Votre emballage</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="package_count">
                  Nombre de colis
                </label>
                <input
                  id="package_count"
                  name="package_count"
                  type="number"
                  min="1"
                  className="tracking-input w-full"
                  value={formData.package_count || 1}
                  onChange={handleChange}
                  
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="package_weight">
                  Poids (kg)
                </label>
                <input
                  id="package_weight"
                  name="package_weight"
                  type="number"
                  step="0.1"
                  min="0.1"
                  className="tracking-input w-full"
                  value={formData.package_weight || 0.5}
                  onChange={handleChange}
                  
                />
                <p className="text-sm text-gray-500 mt-1">
                  Limite: 0,5 kg par enveloppe FedEx
                </p>
              </div>
            </div>
          </TabPanel>
        </Tabs>
        
        <div className="flex justify-between mt-8">
          <AdminButton
            type="outline"
            onClick={() => {
              toast('Opération annulée');
              navigate('/admin/shipments');
            }}
            disabled={loading}
          >
            Annuler
          </AdminButton>

          <button 
            type="button"
            className="bg-white border border-blue-500 text-blue-600 hover:bg-blue-50 px-8 mr-4 py-2 rounded-md flex items-center justify-center transition-colors duration-200"
            disabled={loading || fedexApiStatus.loading}
            onClick={async (e) => {
              e.preventDefault();
              try {
                await validateFedExShipment();
              } catch (error) {
                console.error('Erreur lors de la validation:', error);
                
                // Vérifier si c'est une erreur de validation FedEx avec des détails
                if (error.response?.data?.details?.errors) {
                  const errorMessages = error.response.data.details.errors
                    .map(err => `• ${err.code}: ${err.message}`)
                    .join('\n');
                  
                  toast.error(
                    <div>
                      <strong>Erreur de validation FedEx :</strong>
                      <pre style={{whiteSpace: 'pre-wrap', margin: '5px 0 0 0'}}>{errorMessages}</pre>
                    </div>,
                    { autoClose: 10000 }
                  );
                } else {
                  // Gestion des autres types d'erreurs
                  toast.error(error.message || "Erreur lors de la validation");
                }
              }
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Vérifier la validité
          </button>
          
          <button 
            type="button"
            className="btn-modern btn-secondary-modern px-8 mr-4 flex items-center justify-center min-w-[180px]"
            disabled={loading || isSubmitting}
            onClick={async (e) => {
              e.preventDefault();
              const result = await updateShipmentOnly();
              if (result.success) {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Mise à jour...
              </>
            ) : (
              'Mettre à jour (sans FedEx)'
            )}
          </button>
          
          <button 
            type="submit"
            className="btn-modern btn-primary-modern px-8 flex items-center justify-center min-w-[180px]"
            disabled={loading || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {isEditMode ? 'Mise à jour...' : 'Enregistrement...'}
              </>
            ) : (
              isEditMode ? 'Mettre à jour avec FedEx' : "Enregistrer l'expédition"
            )}
          </button>
        </div>
      </form>

      {/* Fenêtre modale pour la sélection des documents à envoyer */}
      {showDocumentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold">Sélectionner des documents à envoyer</h3>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-600"
                onClick={handleCloseDocumentModal}
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-4 border-b border-gray-200">
              <input
                type="text"
                className="tracking-input w-full"
                placeholder="Rechercher un document..."
                value={searchDocument}
                onChange={(e) => setSearchDocument(e.target.value)}
              />
            </div>
            
            <div className="overflow-y-auto flex-grow p-4">
              {consularActs.length === 0 ? (
                <p className="text-center text-gray-500">Aucun acte consulaire trouvé</p>
              ) : (
                consularActs
                  .filter(act => {
                    const searchLower = searchDocument.toLowerCase();
                    return (
                      act.title.toLowerCase().includes(searchLower)
                    );
                  })
                  .map(act => {
                    const isSelected = formData.documents_to_be_sent.some(doc => doc.act_id === act.id);
                    return (
                      <div key={act.id} className="flex items-center mb-3 p-2 hover:bg-gray-50 rounded">
                        <input
                          type="checkbox"
                          id={`document-${act.id}`}
                          checked={isSelected}
                          onChange={() => {
                            if (isSelected) {
                              // Trouver l'index du document à supprimer
                              const index = formData.documents_to_be_sent.findIndex(doc => doc.act_id === act.id);
                              if (index !== -1) {
                                handleRemoveDocument(index);
                              }
                            } else {
                              handleAddDocument(act);
                            }
                          }}
                          className="mr-3 h-5 w-5 text-blue-600"
                        />
                        <label htmlFor={`document-${act.id}`} className="flex-grow cursor-pointer">
                          <div className="font-medium">{act.title}</div>
                        </label>
                      </div>
                    );
                  })
              )}
            </div>
            
            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button
                type="button"
                className="btn-modern btn-primary-modern px-4 py-2"
                onClick={handleCloseDocumentModal}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Fenêtre modale pour la sélection des membres de famille */}
      {showFamilyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold">Sélectionner des membres de famille</h3>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-600"
                onClick={() => { setShowFamilyModal(false); setShowNewFamilyForm(false); }}
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-4 border-b border-gray-200 space-y-3">
              {/* Recherche */}
              <input
                type="text"
                className="tracking-input w-full"
                placeholder="Rechercher un membre..."
                value={searchFamily}
                onChange={(e) => setSearchFamily(e.target.value)}
              />
              {/* Toggle formulaire nouveau membre */}
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-700">Liste des membres existants</div>
                <button
                  type="button"
                  className="btn-modern btn-outline-modern"
                  onClick={() => setShowNewFamilyForm(v => !v)}
                >
                  {showNewFamilyForm ? 'Masquer le formulaire' : 'Nouveau membre'}
                </button>
              </div>
            </div>
            
            <div className="overflow-y-auto flex-grow p-4">
              {/* Formulaire création (toggle) - placé dans la zone scrollable pour être visible */}
              {showNewFamilyForm && (
                <div className="mb-4">
                  <div className="bg-gray-50 rounded-md p-3 border border-gray-200">
                    <div className="text-sm font-medium text-gray-800 mb-2">Ajouter un membre de famille</div>
                    <form className="grid grid-cols-1 md:grid-cols-2 gap-3" onSubmit={handleCreateFamilyMemberInline}>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Nom *</label>
                        <input
                          className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                          value={newFamily.lastname}
                          onChange={(e) => setNewFamily(prev => ({ ...prev, lastname: e.target.value }))}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Prénom *</label>
                        <input
                          className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                          value={newFamily.firstname}
                          onChange={(e) => setNewFamily(prev => ({ ...prev, firstname: e.target.value }))}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Date de naissance *</label>
                        <input
                          type="date"
                          className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                          value={newFamily.birth_date}
                          max={new Date().toISOString().slice(0, 10)}
                          onChange={(e) => setNewFamily(prev => ({ ...prev, birth_date: e.target.value }))}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Email</label>
                        <input
                          type="email"
                          className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                          value={newFamily.email}
                          onChange={(e) => setNewFamily(prev => ({ ...prev, email: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Téléphone</label>
                        <input
                          className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                          value={newFamily.phone}
                          onChange={(e) => setNewFamily(prev => ({ ...prev, phone: e.target.value }))}
                        />
                      </div>
                      <div className="md:col-span-2 flex justify-end">
                        <button
                          type="submit"
                          className={`btn-modern btn-primary-modern ${isCreatingFamily ? 'opacity-70 cursor-not-allowed' : ''}`}
                          disabled={isCreatingFamily}
                        >
                          {isCreatingFamily ? 'Création…' : 'Créer le membre'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
              {familyMembers.length === 0 ? (
                <p className="text-center text-gray-500">Aucun membre de famille trouvé</p>
              ) : (
                familyMembers
                  .filter(member => {
                    const searchLower = searchFamily.toLowerCase();
                    return (
                      member.firstname.toLowerCase().includes(searchLower) ||
                      member.lastname.toLowerCase().includes(searchLower) ||
                      (member.email && member.email.toLowerCase().includes(searchLower))
                    );
                  })
                  .map(member => {
                    const isSelected = selectedFamilyMembers.some(selected => selected.id === member.id);
                    return (
                      <div key={member.id} className="flex items-center mb-3 p-2 hover:bg-gray-50 rounded">
                        <input
                          type="checkbox"
                          id={`family-member-${member.id}`}
                          checked={isSelected}
                          onChange={() => {
                            if (isSelected) {
                              setSelectedFamilyMembers(selectedFamilyMembers.filter(m => m.id !== member.id));
                            } else {
                              setSelectedFamilyMembers([...selectedFamilyMembers, member]);
                            }
                          }}
                          className="mr-3 h-5 w-5 text-blue-600"
                        />
                        <label htmlFor={`family-member-${member.id}`} className="flex-grow cursor-pointer">
                          <div className="font-medium">{member.firstname} {member.lastname}</div>
                          {member.birth_date && <div className="text-sm text-gray-600">Né(e) le {member.birth_date.split('-').reverse().join('/')}</div>}
                          {member.email && <div className="text-sm text-gray-600">{member.email}</div>}
                          {member.phone && <div className="text-sm text-gray-500">{member.phone}</div>}
                        </label>
                      </div>
                    );
                  })
              )}
            </div>
            
            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button
                type="button"
                className="btn-modern btn-outline-modern mr-2"
                onClick={() => { setShowFamilyModal(false); setShowNewFamilyForm(false); }}
              >
                Annuler
              </button>
              <button
                type="button"
                className="btn-modern btn-primary-modern"
                onClick={() => {
                  // Mettre à jour le formData avec les membres sélectionnés
                  setFormData(prev => ({
                    ...prev,
                    family_members: selectedFamilyMembers.map(member => ({
                      id: member.id,
                      firstname: member.firstname,
                      lastname: member.lastname,
                      birth_date: member.birth_date
                    })),
                    with_family: selectedFamilyMembers.length > 0
                  }));
                  setShowFamilyModal(false);
                  toast.success(`${selectedFamilyMembers.length} membre(s) de famille ajouté(s)`);
                }}
              >
                Confirmer ({selectedFamilyMembers.length})
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modale pour ajouter un paiement */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-medium">Ajouter un paiement</h3>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-500"
                onClick={() => setShowPaymentModal(false)}
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-4">
              {/* Première ligne: Montant et ID de transaction */}
              <div className="flex space-x-4 mb-4">
                <div className="flex-1">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="payment-amount">
                    Montant (€)
                  </label>
                  <input
                    type="number"
                    id="payment-amount"
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    value={paymentData.amount}
                    onChange={(e) => setPaymentData({...paymentData, amount: e.target.value})}
                    step="0.01"
                    min="0"
                    required
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="transaction-id">
                    ID de transaction
                  </label>
                  <input
                    type="text"
                    id="transaction-id"
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline bg-gray-100"
                    value={paymentData.transaction_id}
                    readOnly
                  />
                </div>
              </div>
              
              {/* Deuxième ligne: Méthode de paiement et Statut */}
              <div className="flex space-x-4 mb-4">
                <div className="flex-1">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="payment-method">
                    Méthode de paiement
                  </label>
                  <select
                    id="payment-method"
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    value={paymentData.payment_method}
                    onChange={(e) => setPaymentData({...paymentData, payment_method: e.target.value})}
                    required
                  >
                    <option value="card">Carte bancaire</option>
                    <option value="cash">Espèces</option>
                  </select>
                </div>
                <div className="flex-1">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="payment-status">
                    Statut du paiement
                  </label>
                  <select
                    id="payment-status"
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    value={paymentData.payment_status}
                    onChange={(e) => setPaymentData({...paymentData, payment_status: e.target.value})}
                    required
                  >
                    <option value="pending">En attente</option>
                    <option value="succeeded">Effectuée</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button
                type="button"
                className="btn-modern btn-outline-modern mr-2"
                onClick={() => setShowPaymentModal(false)}
              >
                Annuler
              </button>
              <button
                type="button"
                className="btn-modern btn-primary-modern"
                onClick={handleSavePayment}
              >
                Enregistrer le paiement
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShipmentForm;
